import client from './client';
import type { WorkItem } from './workItems';

export type DocumentStatus = 'uploaded' | 'processing' | 'processed' | 'partial' | 'failed' | 'deleted' | 'missing' | 'unknown';

export interface DocumentRecord {
    id: string;
    user_id: number;
    filename: string;
    mime_type: string;
    size_bytes: number;
    storage_path: string;
    status: DocumentStatus;
    doc_type?: string | null;
    extracted_json?: Record<string, any> | null;
    error_message?: string | null;
    created_at?: string | null;
    processed_at?: string | null;
    expires_at?: string | null;
    mode?: string;
    total_pages?: number;
    source_type?: string;
    version?: number;
}

export interface UploadSlot {
    page_number: number;
    storage_path: string;
}

export interface DocumentInitRequest {
    filename: string;
    mime_type: string;
    size_bytes: number;
    page_count?: number;
    source_type?: 'upload' | 'camera' | 'batch';
}

export interface DocumentInitResponse {
    document_id: string;
    storage_path: string;
    upload_slots?: UploadSlot[] | null;
    total_pages?: number;
}

export interface DocumentProcessRequest {
    intent?: string;
    hint_domain?: string;
    mode?: string;
}

export interface DocumentProcessResponse {
    document: DocumentRecord;
    work_item?: WorkItem;
    work_items?: WorkItem[];
}

export interface DocumentStatusResponse {
    status: string;
    total_pages: number;
    pages_completed: number;
    pages_failed: number;
    error_message?: string | null;
}

export interface DocumentPageInfo {
    id: string;
    document_id: string;
    page_number: number;
    mime_type: string;
    status: string;
    has_ocr: boolean;
    error_message?: string | null;
    processed_at?: string | null;
    version: number;
}

export interface DocumentDownloadResponse {
    url: string;
}

export interface DocumentErrorResponse {
    code: 'DOCUMENT_DELETED' | 'DOCUMENT_MISSING' | 'AI_CREDITS_EXHAUSTED' | 'DOCUMENT_AI_UNAVAILABLE' | 'PAGE_LIMIT_EXCEEDED' | 'ANALYTICAL_NOT_AVAILABLE' | string;
    message: string;
    remaining?: number;
    reset_date?: string | null;
    upgrade_url?: string;
    max_pages?: number;
    requested_pages?: number;
}


export interface DocumentListItem {
    id: string;
    filename: string;
    mime_type: string;
    size_bytes: number;
    status: DocumentStatus;
    doc_type?: string | null;
    error_message?: string | null;
    created_at?: string | null;
    processed_at?: string | null;
    work_item_id?: string | null;
    /** Number of work items still linked to this document (for multi-task extraction batches). */
    work_items_count?: number;
    requires_human_review?: boolean;
    review_reasons?: string[];
    has_guidance?: boolean;
    calm_summary?: string | null;
    safe_first_step?: string | null;
    scam_risk_level?: string | null;
    total_pages?: number;
    mode?: string;
}

export interface DocumentListResponse {
    items: DocumentListItem[];
    total: number;
    page: number;
    limit: number;
    pages: number;
}

export interface DocumentAskResponse {
    answer: string;
    source_pages: number[];
    confidence?: number | null;
    source_chunks?: Array<{
        chunk_index: number;
        page_number?: number | null;
        score: number;
    }> | null;
}

export interface DocumentClause {
    clause_index: number;
    title: string;
    text: string;
    risk_level: 'low' | 'medium' | 'high';
    risk_reason?: string | null;
    page_range?: number[] | null;
    entities?: string[] | null;
}

export interface DocumentClauseResult {
    clauses: DocumentClause[];
    overall_risk_score?: number | null;
    risk_summary?: string | null;
    key_entities?: Array<{ name: string; type: string }> | null;
    cross_references?: Array<{ from: number; to: number; reason: string }> | null;
}

export interface DocumentExtractionItem {
    id: string;
    extraction_type: string;
    result_json: Record<string, any>;
    model_used?: string | null;
    prompt_tokens?: number | null;
    completion_tokens?: number | null;
    created_at?: string | null;
    is_current: boolean;
}

export const documentsApi = {
    initDocument: async (payload: DocumentInitRequest): Promise<DocumentInitResponse> => {
        const response = await client.post('/documents/init', payload);
        return response.data;
    },

    processDocument: async (documentId: string, payload: DocumentProcessRequest): Promise<DocumentProcessResponse & { async?: boolean; job_id?: string; poll_url?: string }> => {
        const response = await client.post(`/documents/${documentId}/process`, payload);
        if (response.status === 202) {
            return { ...response.data, async: true } as any;
        }
        return response.data;
    },

    getDocumentStatus: async (documentId: string): Promise<DocumentStatusResponse> => {
        const response = await client.get(`/documents/${documentId}/status`);
        return response.data;
    },

    getDocumentPages: async (documentId: string): Promise<DocumentPageInfo[]> => {
        const response = await client.get(`/documents/${documentId}/pages`);
        return response.data;
    },

    getPageDownloadUrl: async (documentId: string, pageNumber: number): Promise<DocumentDownloadResponse> => {
        const response = await client.get(`/documents/${documentId}/pages/${pageNumber}/download-url`);
        return response.data;
    },

    reprocessDocument: async (documentId: string, pageNumbers?: number[]): Promise<{ message: string; page_numbers: number[] }> => {
        const response = await client.post(`/documents/${documentId}/reprocess`, {
            page_numbers: pageNumbers ?? null,
        });
        return response.data;
    },

    getDocument: async (documentId: string): Promise<DocumentRecord> => {
        const response = await client.get(`/documents/${documentId}`);
        return response.data;
    },

    getDocuments: async (page = 1, limit = 20, type?: string): Promise<DocumentListResponse> => {
        const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
        if (type) params.append('type', type);
        const response = await client.get(`/documents?${params.toString()}`);
        return response.data;
    },

    getDownloadUrl: async (documentId: string): Promise<DocumentDownloadResponse> => {
        const response = await client.get(`/documents/${documentId}/download-url`);
        return response.data;
    },

    askDocument: async (documentId: string, question: string): Promise<DocumentAskResponse> => {
        const response = await client.post(`/documents/${documentId}/ask`, { question });
        return response.data;
    },

    getDocumentClauses: async (documentId: string): Promise<DocumentClauseResult> => {
        const response = await client.get(`/documents/${documentId}/clauses`);
        return response.data;
    },

    getDocumentExtractions: async (documentId: string): Promise<DocumentExtractionItem[]> => {
        const response = await client.get(`/documents/${documentId}/extractions`);
        return response.data;
    },

    deleteDocument: async (documentId: string): Promise<void> => {
        await client.delete(`/documents/${documentId}`);
    },
};

export default documentsApi;
